import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { TokenService } from '../token.service';
import { environment } from '../../environments/environment';
import { GetProjectIdService } from '../getProjectId.service';
import { TenderingService } from '../tendering-services/tendering.service';

import { ProjectStageApproverResponse } from '../../interfaces/project-stage-approver.interface';
@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private apiUrl = environment.apiUrl;
  private token: string | null = null;
  private projectId: any = localStorage.getItem('projectId');
  private subscription: Subscription = new Subscription();
  projectIdNew: any;

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private getProjectIdService: GetProjectIdService,
    private tenderingService: TenderingService
  ) {}

  // Method to dynamically set headers with token
  private getHeaders(): HttpHeaders {
    const token = this.tokenService.getToken(); // Fetch the latest token
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : '',
      // 'Content-Type': 'application/json',
    });
  }

  getFiles() {
    throw new Error('Method not implemented.');
  }

  getAllProjects(): Observable<any> {
    return this.http.get(`${this.apiUrl}/projects`, {});
  }

  getDataViewMode(revisionId: number) {
    let projectId = this.getProjectIdService.getProjectServiceId();
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
    }

    return this.http.get(
      `${this.apiUrl}/get-project-specific-version/${projectId}/${revisionId}`,
      {}
    );
  }

  getProjectDetails(currentProjectId: number = 0) {
    let projectId = null;
    if (!currentProjectId) {
      projectId = this.getProjectIdService.getProjectServiceId();
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
      }
    } else {
      projectId = currentProjectId;
    }

    return this.http.get(`${this.apiUrl}/projects/${projectId}`);
  }

  // This function will be used in all tendering to get project selected work packages
  getProjectSelectedWorkPackages(projectId: number) {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/selected-work-packages`
    );
  }

  addNewVersionOfProject() {
    const projectId = this.getProjectIdService.getProjectId();

    return this.http.get(
      `${this.apiUrl}/projects/new-version/${projectId}`,
      {}
    );
  }

  // add project api calls
  addProjectInfo(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/brief/intro`, data, {});
  }

  // update project api calls
  updateProjectInfo(
    data: any,
    currentProjectId: number | null = null
  ): Observable<any> {
    let projectId: number | null;
    if (!currentProjectId) {
      projectId = parseInt(this.getProjectIdService.getProjectId());
    } else {
      projectId = currentProjectId;
    }

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}?_method=PUT`,
      data
    );
  }

  // Project Creation Second Step Update Operation
  updateProjectBrief(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/brief/intro`, data);
  }

  // Project Creation - Step # 2
  updateProjectLocationAndSiteConditionsDetails(
    data: any,
    currentProjectId: number | null = null
  ): Observable<any> {
    let projectId: number | null;
    if (!currentProjectId) {
      projectId = parseInt(this.getProjectIdService.getProjectId());
    } else {
      projectId = currentProjectId;
    }

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/brief/location-details?_method=PUT`,
      data
    );
  }

  // Project Creation - Step # 3
  updateProjectBUdgetAndTimelineDetails(
    data: any,
    currentProjectId: number | null = null
  ): Observable<any> {
    let projectId: number | null;
    if (!currentProjectId) {
      projectId = parseInt(this.getProjectIdService.getProjectId());
    } else {
      projectId = currentProjectId;
    }

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/brief/budget-timeline-details?_method=PUT`,
      data
    );
  }

  updateProjectStakeHolder(data, projectId: number): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/project-stake-holders/create`,
      data
    );
  }

  // update project api calls
  updateMilestoneProject(data: any, milestoneId: any): Observable<any> {
    // const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/project-milestones/${milestoneId}?_method=PUT`,
      data
    );
  }

  addProjectContract(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects`, data, {
      // headers: this.headers,
      // headers: this.getHeaders(),
    });
  }

  addProjectContractor(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-contractors`, data, {});
  }

  addProjectSupplier(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-suppliers`, data, {});
  }

  editProject(id: any, data: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${id}?_method=PUT`,
      data,
      {}
    );
  }

  deleteProject(id: any): Observable<any> {
    return this.http.delete(`${this.apiUrl}/projects/${id}?_method=DELETE`, {});
  }

  editSingleFieldofProject(
    data: any,
    CurrentProjectId: number | null
  ): Observable<any> {
    let projectId: number | null;
    if (!CurrentProjectId) {
      projectId = parseInt(this.getProjectIdService.getProjectId());
    } else {
      projectId = CurrentProjectId;
    }
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/update-field?_method=PUT`,
      data,
      {}
    );
  }

  // add project design api calls
  addDesign(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-designs`, data, {});
  }

  // add project risk api calls
  addRisk(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/risk-registers`, data, {});
  }

  getRiskCategories(): Observable<any> {
    return this.http.get(`${this.apiUrl}/risk-categories`);
  }

  viewRisk(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/risk-registers?project_id=${projectId}`,
      {}
    );
  }

  // add project scope api calls
  addScope(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/store-project-scope`, data, {});
  }

  // add project requirement api calls
  addRequirement(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-requirements`, data, {});
  }

  // add project permits api calls
  addPermit(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-permits`, data, {});
  }

  // add project goal api calls
  addGoal(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-goals`, data, {
      // headers: this.getHeaders(),
    });
  }

  addWorkPackageStandard(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/work-packages`, data, {
      // headers: this.getHeaders(),
    });
  }

  sendApproval(data: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/assign-project-stage-approvers`,
      data,
      {
        // headers: this.getHeaders(),
      }
    );
  }

  getCurrentStageApproval(data: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/get-project-stage-approvers/${data.project_id}/${data.stage}`,
      data,
      {
        // headers: this.getHeaders(),
      }
    );
  }

  getProjectBriefDetails(id: any): Observable<any> {
    return this.http.get(`${this.apiUrl}/projects/${id}`, {});
  }

  addWorkPackageStandardItem(data: any) {
    return this.http.post(`${this.apiUrl}/work-package-standard-items`, data, {
      // headers: this.headers,
    });
  }

  getAllWPS(): Observable<any> {
    return this.http.get(`${this.apiUrl}/work-package-standards`, {
      // headers: this.headers,
    });
  }

  // get all work packages
  getAllWPSI(): Observable<any> {
    return this.http.get(`${this.apiUrl}/work-packages?parent=1`, {});
  }

  // get all work packages
  getSavedWorkPackagesDetails(projectId: number | string): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/project-work-packages/details`,
      {}
    );
  }

  createWorkBreakDownStandard(data: any) {
    return this.http.post(
      // `${this.apiUrl}/store-project-work-package-details`,
      `${this.apiUrl}/update-project-work-package-details`,

      data,
      {
        // headers: this.headers,
      }
    );
  }

  deleteWorkPackageStandard(id: any): Observable<any> {
    return this.http.delete(
      `${this.apiUrl}/work-package-standards/${id}?_method=DELETE`,
      {
        // headers: this.getHeaders(),
      }
    );
  }
  getAllProjectTypes(): Observable<any> {
    return this.http.get(`${this.apiUrl}/project-types?parent=1`, {
      // headers: this.headers,
    });
  }
  getAllProjectSubTypes(Id: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-types/get-child/${Id}`, {
      // headers: this.headers,
    });
  }
  createCustomProjectTypes(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-types`, data);
  }

  getResponsibilityMatrix() {
    let projectId = '';
    this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
    });
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectId();
    }
    return this.http.post(
      `${this.apiUrl}/get-responsibility-matrix-details?project_id=${projectId}`,
      {
        // headers: this.headers,
      }
    );
  }

  getRiskRegister() {
    let projectId = '';
    this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
    });
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectId();
    }
    return this.http.get(
      `${this.apiUrl}/risk-registers?project_id=${projectId}`,
      {}
    );
  }

  getGrades() {
    return this.http.get(`${this.apiUrl}/grades`);
  }
//Start drawings */

  drawingSet(projectId: number): Observable<any> {
    const url = `${this.apiUrl}/projects/${projectId}/drawing-set`;
    return this.http.get(url);
}
  addDesignDocument(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/store-drawings`, data, {
      // headers: this.getHeaders(),
    });
  }
  addCustomSet(data: any,): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/store-drawing-set`, data, {
      // headers: this.getHeaders(),
    });
  }
  downloadDocument(id: number, title: any): Observable<HttpResponse<Blob>> {
    const url = `${this.apiUrl}/projects/download-drawing/${id}?title=${encodeURIComponent(title)}`;
    return this.http.get(url, {
      responseType: 'blob',  // Important for binary file download
      observe: 'response'    // Required to access headers
    });
  }
  

 getDesignDocuments(document_type: string): Observable<any> {
  let projectId: any;

  // Subscribe to the projectId$ observable to get the project ID
  this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
    projectId = id;
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectId();
    }
  });

  if (!projectId) {
    return new Observable((observer) => {
      observer.error('Project Id is not available, no drawings');
    });
  }
  // Make the HTTP GET request with document_type as a query parameter
  const url = `${this.apiUrl}/projects/${projectId}/get-drawing-type/${document_type}`;
  return this.http.get(url);
}
  
deleteDrawing(id: any): Observable<any> {
  return this.http.delete(`${this.apiUrl}/projects/delete-drawing/${id}`);
}

// End Drawings*/
  checkToken(): Observable<any> {
    return this.http.get(`${this.apiUrl}/user`, {});
  }

  addcalculations(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/store-drawings`, data, {
      // headers: this.getHeaders(),
    });
  }

  getcalculations(): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/fetch-document/${this.projectId}/calculations`,
      {
        // headers: this.getHeaders(),
      }
    );
  }

  getdesignsignoff(): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/fetch-document/${this.projectId}/design_signoff`,
      {
        // headers: this.getHeaders(),
      }
    );
  }
  getProjectRevision(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
    return this.http.get(
      `${this.apiUrl}/get-project-versions-count/${projectId}`
    );
  }

  getAddressByZipCode(zipCode: any, country: any) {
    return this.http.get(
      `${this.apiUrl}/geocode?zipcode=${zipCode}&country=${country}`,
      {
        // headers: this.headers,
      }
    );
  }

  getCountry(id: any): Observable<any> {
    return this.http.get(`${this.apiUrl}/countries/${id}`, {
      // headers: this.headers,
    });
  }

  private countriesSubject = new BehaviorSubject<any[]>([]); // Initialize a BehaviorSubject

  getCountries(): Observable<any[]> {
    this.http.get<any[]>(`${this.apiUrl}/countries`).subscribe((countries) => {
      this.countriesSubject.next(countries); // Update the BehaviorSubject with the fetched countries
    });
    return this.countriesSubject.asObservable(); // Return an Observable of the countries
  }
  updateOrganizationCountry(orgId: number, countryId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/organizations/${orgId}?_method=PUT`, {
      country_id: countryId,
    });
  }

  saveWorkPackages(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/project-work-packages`, data, {
      // headers: this.getHeaders(),
    });
  }

  saveCostEstimate(data: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/update-project-work-package-details`,
      data,
      {
        // headers: this.getHeaders(),
      }
    );
  }
  fetchProjecttPackages(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(`${this.apiUrl}/getWorkPackages/${{ projectId }}`);
  }

  // ########################################## STAGE 2 STARTS ##########################################
  /**
   * when the component loads, get details based on project_id and stage from post api get-project-stage-approvers/{id}/{stage}.
   * if we get response from the post api (), show project-stage-approval component else sendforapproval component
   * project-stage-approval component
   *    if logged in user_id is also present in the list returned in the api
   *      show a button with a dropdown items to select from. approve,reject,pending
   *    else if logged in user_id is not present in the list
   *      show list with just user name and status fields disabled showing pending or other status returned from the api
   */

  getProjectStageApprovals(
    projectId: number,
    stage: number
  ): Observable<ProjectStageApproverResponse> {
    /**
     * TODO: this needs to be dynamic
     */
    return this.http.post<ProjectStageApproverResponse>(
      `${this.apiUrl}/get-project-stage-approvers/${projectId}/${stage}`,
      {}
    );
  }

  updateApprovalStatus(
    projectId: number,
    userId: number,
    status: any,
    comments?: string
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/update-project-stage-approvers`,
      {
        project_id: projectId,
        user_id: userId,
        stage: 1,
        status: status,
        comments: comments || null,
      }
    );
  }

  getCurrentStageNumber() {
    const stagesStr = localStorage.getItem('stages');
    let stages = {};
    if (stagesStr) {
      stages = stagesStr ? JSON.parse(stagesStr) : null;
    }
    if (stages === null) {
      return null;
    }

    const firstTrueKey = Object.entries(stages).find(([, value]) => value)?.[0];

    if (!firstTrueKey) {
      throw new Error('No key with a true value found.');
    }
    const lastDigit = Number(firstTrueKey.slice(-1));

    if (isNaN(lastDigit)) {
      throw new Error('The last character is not a digit.');
    }

    return lastDigit;
  }

  isStageTwoCompleted(data: any): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/get-stage-completion/${data.project_id}/${data.stage}`,
      {},
      {}
    );
  }

  // ########################################## STAGE 2 ENDS ##########################################

  // ########################################## Tendering Section Start ##########################################

  // ########################################## EOI Start ##########################################

  fetchEoiStep2Data(): Observable<any> {
    const eoi_id = localStorage.getItem('eoi_id');
    const projectId = this.getProjectIdService.getProjectId();
    if (eoi_id && projectId) {
      return this.http.get(
        `${this.apiUrl}/e-o-i-wp-details/${projectId}/${eoi_id}`
      );
    }
  }

  getPreInitiationData(): Observable<any> {
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(`${this.apiUrl}/eoi-pre-initiation-data/${projectId}`);
  }

  getSubmittedInitiationData(): Observable<any> {
    let pqqId: any;
    this.tenderingService.pqqId$.subscribe((id) => {
      pqqId = id;
    });
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(
      `${this.apiUrl}/eoi-submitted-initiation-data/${projectId}/${pqqId}`
    );
  }

  getSubmittedInitiationDataPQQ(): Observable<any> {
    const pqqId = this.tenderingService.getPqqId();
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(
      `${this.apiUrl}/pqq/submitted/initiation/data/${projectId}/${pqqId}`
    );
  }

  initiateEOI(): Observable<any> {
    let eoiId: any;
    this.tenderingService.eoiId$.subscribe((id) => {
      eoiId = id;
      if (!eoiId) {
        eoiId = localStorage.getItem('eoi_id');
      }
    });
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/initiate-e-o-i/${projectId}/${eoiId}`,
      {}
    );
  }

  projectExpressions(data: any): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });
    return this.http.post(
      `${this.apiUrl}/e-o-i-store-response/${projectId}`,
      data
    );
  }

  updateProjectExpressions(data: any, id: any): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/e-o-i-store-response/${projectId}/${id}?_method=PUT`,
      data
    );
  }
  getProjectExpressions(): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(`${this.apiUrl}/expression-of-interests/${projectId}`);
  }

  closeEOI(eoi_id: any): Observable<any> {
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/e-o-i-halt-response/${projectId}/${eoi_id}`,
      {}
    );
  }

  getApplicantEoiView(data: any, eoiResponseId: any): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(
      `${this.apiUrl}/eoi-sender-detail-view/${eoiResponseId}/${projectId}`,
      data
    );
  }

  // ########################################## EOI End ##########################################

  // ########################################## PQQ Start ##########################################

  preQualificationQuestionnaire(data: any): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(`${this.apiUrl}/project-p-q-q/${projectId}`, data);
  }

  projectPQQInitiateStep1(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/p-q-q-organization-details/${projectId}`,
      data
    );
  }

  projectPQQInitiateStep2(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(`${this.apiUrl}/p-q-q-wp-details/${projectId}`, data);
  }

  updateProjectPQQ(data: any, id: any): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/expression-of-interests/${projectId}/${id}?_method=PUT`,
      data
    );
  }
  // TODO: //
  getPqq(pqqId: number, responseId: number): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/pqq/${pqqId}/response/${responseId}`,
      {}
    );
  }

  getPqqSubmissions(): Observable<any> {
    let projectId: any;

    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.get(
      `${this.apiUrl}/pqq/organization/listing/view/${projectId}`
    );
  }

  initiatePQQ(): Observable<any> {
    let pqqId: any;
    this.tenderingService.pqqId$.subscribe((id) => {
      pqqId = id;
    });
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/initiate/pqq/${projectId}/${pqqId}`,
      {}
    );
  }

  // ########################################## PQQ End ##########################################

  // ########################################## PQQ Response APIs Start ##########################################

  projectPQQResponseStep1(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();
    return this.http.post(
      `${this.apiUrl}/store/pqq/company-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep2(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/health-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep3(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/construction-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep4(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/qms-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep5(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/financial-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep6(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/legal-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep7(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/sustainability-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep8(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/reference-details/${projectId}/${pqqId}`,
      data
    );
  }

  projectPQQResponseStep9(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/store/pqq/signatory-details/${projectId}/${pqqId}`,
      data
    );
  }

  PQQSubmittedResponseData(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/get/pqq/response/${projectId}/${pqqId}`,
      {}
    );
  }

  PQQSubmitResponse(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const pqqResponseId = this.tenderingService.getPqqId();

    return this.http.post(
      `${this.apiUrl}/pqq/submit/response/${projectId}/${pqqResponseId}`,
      {}
    );
  }

  // ########################################## PQQ Response APIs End ##########################################

  // ########################################## Tendering Section End ##########################################

  // ########################################## Projects Reuqests Start ##########################################

  getProjectsRequests(): Observable<any> {
    return this.http.get(`${this.apiUrl}/get-projects-with-eoi-requests`, {});
  }

  // ########################################## Projects Reuqests End ##########################################

  // ########################################## Internal Approvers for EOI and PQQ Start ##########################################

  sendApprovalRequest(
    project_Id: any,
    ResponseId: any,
    data: any,
    modalType: string
  ): Observable<any> {
    const projectId = project_Id;

    return this.http.post(
      `${this.apiUrl}/${modalType}/send-for-approval/initiate/${projectId}/${modalType}/${ResponseId}`,
      data
    );
  }

  sendApprovalResponse(
    project_Id: any,
    ResponseId: any,
    data: any,
    modalType: string
  ): Observable<any> {
    const projectId = project_Id;

    return this.http.post(
      `${this.apiUrl}/${modalType}/send-for-approval/store-response/${projectId}/${modalType}/${ResponseId}`,
      data
    );
  }

  getApprovalsStatus(
    project_Id: any,
    eoiResponseId: any,
    modalType: string
  ): Observable<any> {
    const projectId = project_Id;

    return this.http.post(
      `${this.apiUrl}/${modalType}/send-for-approval/get-responses/${projectId}/${modalType}/${eoiResponseId}`,
      {}
    );
  }

  getProjectCountry(project_id: any): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${project_id}/country/project-country`
    );
  }

  getProjectDesignDocuments(projectId: number, type: string): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/design/${type}/list`
    );
  }
  // ########################################## Internal Approvers for EOI and PQQ End ##########################################

  fetchCurrentStageApprovalPercentage(
    project_id: any,
    stage: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${project_id}/approval/stage/${stage}/status`
    );
  }
  resendApproval(userId: number): Observable<any> {
    let projectId = this.getProjectIdService.getProjectServiceId();

    if (!projectId) {
      projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
    }
    return this.http.post<any>(
      `${this.apiUrl}/projects/${projectId}/users/${userId}/update-status`,
      {}
    );
  }
}
